import React, { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  BellPlusIcon,
  CalendarIcon,
  CircleCheckIcon,
  EllipsisIcon,
  SquareArrowOutUpRightIcon,
  TrashIcon,
} from 'lucide-react'
import { toast } from 'sonner'

import { isDateField } from '@/lib/json-schema-utils.ts'
import { useModalState } from '@/hooks/use-modal-state.ts'
import { useSmartForm } from '@/hooks/use-smart-form.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { confirm } from '@/components/confirmation.tsx'
import { CalendarEventDialog } from '@/features/calendar/components/calendar-event-dialog.tsx'
import { useDeleteCalendarEvent } from '@/features/calendar/hooks/use-delete-calendar-event.ts'
import { getDocumentQueryOptions } from '@/features/documents/hooks/use-get-document.ts'
import { Document } from '@/features/documents/types.ts'

export const DocumentAttributeActions: React.FC<{
  documentAttribute: Document['attributes'][number]
  document?: Document
  level?: string
}> = ({ documentAttribute, document, level }) => {
  const queryClient = useQueryClient()

  const form = useSmartForm<{ title: string }>()
  useEffect(() => {
    form.reset({
      title: `${documentAttribute.attribute.name} - ${document?.name}`,
    })
  }, [document?.name, documentAttribute.attribute.name])
  const addEventDialogCtrl = useModalState()
  const handleAddEventBtnClick = () => addEventDialogCtrl.open()

  const handleCalendarEventCreated = async () => {
    queryClient.invalidateQueries({
      queryKey: getDocumentQueryOptions({ documentId: document!.id }).queryKey,
    })

    addEventDialogCtrl.close()
  }

  const deleteCalendarEvent = useDeleteCalendarEvent({
    onSuccess() {
      toast('Evento calendario rimosso', { icon: <CircleCheckIcon /> })

      return queryClient.invalidateQueries({
        queryKey: getDocumentQueryOptions({ documentId: document!.id })
          .queryKey,
      })
    },
  })

  const handleDeleteEventBtnClick = () =>
    confirm({
      title: 'Elimina evento',
      content: "Sei sicuro di voler rimuovere l'evento dal calendario?",
      okButtonVariant: 'destructive',
      okText: 'Elimina',
      async onOk() {
        await deleteCalendarEvent.mutateAsync({
          eventId: documentAttribute.calendar_events[0]!.id,
        })
      },
    })

  return (
    <>
      <div className="flex gap-x-1">
        {documentAttribute.calendar_events?.[0] && (
          <Popover>
            <PopoverTrigger asChild>
              <div className="cursor-pointer">
                <Badge variant="secondary" className="h-6 text-xs">
                  <CalendarIcon className="size-3" />
                </Badge>
              </div>
            </PopoverTrigger>
            <PopoverContent>
              <h2 className="mb-2 font-medium leading-none">
                Evento Calendario
              </h2>
              <p className="text-muted-foreground text-sm">
                È presente un evento nel calendario per questa data.
                <Link
                  className="text-gray-900"
                  to="/calendar"
                  search={{
                    eventId: documentAttribute.calendar_events?.[0]?.id,
                  }}
                >
                  <SquareArrowOutUpRightIcon className="ml-2 inline size-4" />
                </Link>
              </p>

              <Separator className="my-4" />

              <Button
                size="sm"
                variant="destructive"
                className="w-full"
                onClick={handleDeleteEventBtnClick}
              >
                <TrashIcon className="size-4" />
                Elimina
              </Button>
            </PopoverContent>
          </Popover>
        )}

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" className="size-6">
              <EllipsisIcon className="size-6" />
              {/*CS {documentAttribute.confidence_score}%*/}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 max-w-xs space-y-4">
            <div>
              {/*<h2 className="mb-2 font-medium leading-none">*/}
              {/*  Confidence Score*/}
              {/*</h2>*/}
              <div className="space-y-1 text-sm">
                {documentAttribute.confidence_score ? (
                  <>
                    <b>Confidence score</b>
                    <p>Livello: {level}</p>
                    <p>Score: {documentAttribute.confidence_score}%</p>
                  </>
                ) : null}

                {documentAttribute.pages?.length > 0 && (
                  <>
                    <p>
                      Pagine:{' '}
                      <span>
                        {documentAttribute.pages.map((p, index) => (
                          <Link
                            key={index}
                            to="/documents/$documentId"
                            params={{ documentId: document?.id as string }}
                            search={(searchParams) => ({
                              ...searchParams,
                              previewMode: searchParams?.previewMode ?? 'image',
                              mode: 'view',
                              pageNum: p!,
                            })}
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              className="mr-1 size-6 p-1 leading-none"
                            >
                              {p}
                            </Button>
                          </Link>
                        ))}
                      </span>
                    </p>
                  </>
                )}
              </div>
            </div>
            {isDateField(documentAttribute.attribute.json_schema) &&
              !documentAttribute.calendar_events?.[0] && (
                <>
                  <Separator />
                  <Button
                    className="w-full"
                    size="sm"
                    variant="outline"
                    onClick={handleAddEventBtnClick}
                  >
                    <BellPlusIcon className="size-4" />
                    Crea evento calendario
                  </Button>
                </>
              )}
          </PopoverContent>
        </Popover>
      </div>

      <CalendarEventDialog
        isOpen={addEventDialogCtrl.isOpen}
        onOpenChange={addEventDialogCtrl.toggle}
        mode="create"
        date={documentAttribute.value}
        title={`${documentAttribute.attribute.name} - ${document?.name}`}
        description={`Per: ${documentAttribute.attribute.name}`}
        onSuccess={handleCalendarEventCreated}
        documentAttributeId={documentAttribute.id}
      />
    </>
  )
}
